import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  MiButton,
  MiDetailFields,
  MiIcon,
  MiSectionHeader,
  MiTab,
} from "@miplatform/components";
import { FONT_WEIGHTS, SECTION_HEADER_VARIANTS } from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { IIssue } from "@miplatform/types";
import { getDefaultDate, groupBy } from "@miplatform/utils";
import {
  mdiChevronDown,
  mdiClipboardTextOutline,
  mdiHistory,
  mdiInformationOutline,
  mdiPencil,
  mdiTag,
} from "@mdi/js";
import { ActivityDateRange, MiActivityLog } from "@mibuildweb/components";
import { useIssue } from "@mibuildweb/hooks";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { useInspectionsContext } from "../../../InspectionsProvider";
import { InspectionIssueCard } from "../../Cards/InspectionIssueCard";

export const InspectionDetails = () => {
  const { classes, theme } = useStyles();

  const {
    selectedInspectionAttempt,
    getInspectionActivityHistory,
    inspectionActivities,
    getInspectionDetails,
    getInspectionAttemptDetails,
    selectedInspection,
    trades,
    tradeOptions,
    inspectionTemplates,
    updateInspectionNotes,
    isLoading: loading,
  } = useInspectionsContext();

  const { updateIssue, deleteIssue } = useIssue();

  const issueCount = selectedInspectionAttempt?.issues?.length ?? 0;

  const detailsTab = { value: "details", label: "Details" };
  const historyTab = {
    value: "history",
    label: "History",
  };
  const redTagsTab = {
    value: "redTags",
    label: "Red Tag Issues",
    icon: (
      <Typography
        color={theme.palette.error.main}
        variant="HD2"
        fontWeight={FONT_WEIGHTS.REGULAR}
      >
        {issueCount ? `(0/${issueCount})` : ""}
      </Typography>
    ),
  };
  const prepWorkTab = { value: "prepWork", label: "Prep Work" };
  const tabs = [detailsTab, redTagsTab, prepWorkTab, historyTab];

  const [selectedTab, setSelectedTab] = React.useState(detailsTab.value);
  const [editNotesFieldset, setEditNotesFieldset] = React.useState(false);
  const [historyDateRange, setHistoryDateRange] = useState<ActivityDateRange>({
    startDate: null,
    endDate: null,
  });

  const edit = useEdit(selectedInspectionAttempt ?? {});

  const { control, getValues, reset } = useForm({
    mode: "onChange",
    defaultValues: {
      notes: selectedInspection?.notes ?? "",
    },
  });

  const tradesMap = useMemo(() => {
    return trades.reduce((acc, trade) => {
      acc[trade.id] = trade;
      return acc;
    }, {} as Record<string, (typeof trades)[0]>);
  }, [trades]);

  const inspectionTemplate = useMemo(
    () =>
      selectedInspectionAttempt
        ? inspectionTemplates[selectedInspectionAttempt?.templateName]
        : undefined,
    [inspectionTemplates, selectedInspectionAttempt?.templateName]
  );

  const inspectionTrade = useMemo(
    () =>
      inspectionTemplate?.tradeId
        ? tradesMap[inspectionTemplate?.tradeId]
        : undefined,
    [tradesMap, inspectionTemplate]
  );

  const detailFields = [
    {
      label: "Builder",
      name: "home.builderName",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Trade",
      value: inspectionTrade?.name,
      name: "tradeName",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Company",
      name: "companyName",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Community",
      name: "communityName",
      width: "25%",
      readOnly: true,
    },
    {
      label: "Scheduled Date",
      name: "approvedStartDate",
      value: getDefaultDate(edit.getValue("approvedStartDate")),
      width: "25%",
      readOnly: true,
    },
    {
      label: "Complete Date",
      name: "inspectedDate",
      value: getDefaultDate(edit.getValue("inspectedDate")),
      width: "25%",
      readOnly: true,
    },
    { label: "Assignee", name: "assigneeName", width: "50%", readOnly: true },
  ];

  const noteFields = [
    {
      label: "Notes",
      name: "notes",
      multiline: true,
      rows: 4,
      readOnly: !editNotesFieldset,
    },
  ];

  const handleSubmitInspectionNotes = async () => {
    if (!selectedInspection) return;
    setEditNotesFieldset(false);
    await updateInspectionNotes({
      id: selectedInspection.id,
      notes: getValues("notes"),
      modifiedOn: selectedInspection.modifiedOn,
    });
    getInspectionDetails(selectedInspection.id);
  };

  const handleUpdateIssue = async (issueId: string, data: Partial<IIssue>) => {
    await updateIssue({ id: issueId, data });
    if (selectedInspectionAttempt) {
      getInspectionAttemptDetails(selectedInspectionAttempt.id);
    }
  };

  const handleDeleteIssue = async (issueId: string, modifiedOn: string) => {
    await deleteIssue({
      issueId,
      modifiedOn,
    });
    if (selectedInspectionAttempt) {
      getInspectionAttemptDetails(selectedInspectionAttempt?.id);
    }
  };

  const handleHistoryDateRangeChange = useCallback(
    (newRange: ActivityDateRange) => {
      setHistoryDateRange(newRange);
    },
    []
  );

  React.useLayoutEffect(() => {
    setHistoryDateRange({ startDate: null, endDate: null });
  }, [
    selectedInspectionAttempt?.referenceId,
    selectedInspectionAttempt?.inspectionId,
  ]);

  useEffect(() => {
    if (selectedInspectionAttempt?.referenceId) {
      const inspectionRef =
        selectedInspectionAttempt.referenceId.split("/assignments")[0];
      getInspectionActivityHistory(inspectionRef, historyDateRange);
    }
  }, [
    selectedInspectionAttempt?.referenceId,
    selectedInspectionAttempt?.inspectionId,
    historyDateRange,
  ]);

  useEffect(() => {
    reset({
      notes: selectedInspection?.notes,
    });
  }, [selectedInspection?.notes]);

  return (
    <Box>
      <Box pb={2}>
        <MiTab
          isSecondary
          tabs={tabs}
          currentTab={selectedTab}
          onTabChange={setSelectedTab}
        />
      </Box>
      <>
        {selectedTab === detailsTab.value && (
          <>
            <Box>
              <MiDetailFields
                detailFields={detailFields}
                headerProps={{
                  title: "Details",
                  icon: mdiInformationOutline,
                  variant: SECTION_HEADER_VARIANTS.UNDERLINED,
                }}
                edit={edit}
              />
            </Box>
            <Box>
              <MiDetailFields
                detailFields={noteFields}
                headerProps={{
                  title: "Notes",
                  icon: mdiClipboardTextOutline,
                  variant: SECTION_HEADER_VARIANTS.UNDERLINED,
                  rightIcon: editNotesFieldset ? null : mdiPencil,
                  onRightIconClick: () => setEditNotesFieldset(true),
                }}
                control={control}
              />
              {editNotesFieldset && (
                <Box display="flex" justifyContent="flex-end" pr={1}>
                  <MiButton
                    title="Cancel"
                    onClick={() => setEditNotesFieldset(false)}
                  />
                  <MiButton
                    title="Save"
                    variant="contained"
                    onClick={handleSubmitInspectionNotes}
                  />
                </Box>
              )}
            </Box>
          </>
        )}
        {selectedTab === redTagsTab.value && (
          <Box display="flex" flexDirection="column" gap={2}>
            <MiSectionHeader
              variant={SECTION_HEADER_VARIANTS.UNDERLINED}
              title="Red Tags"
              leftIcon={mdiTag}
              leftIconColor="error"
            />
            <Box display="flex" flexDirection="column" gap={2}>
              {Object.entries(
                groupBy(
                  selectedInspectionAttempt?.issues || [],
                  (issue: IIssue) => getDefaultDate(issue.createdOn)
                ) as Record<string, IIssue[]>
              )?.map(([createdOn, issues]) => (
                <Accordion
                  key={createdOn}
                  defaultExpanded
                  className={classes.accordion}
                  disableGutters
                >
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={<MiIcon path={mdiChevronDown} size={0.75} />}
                  >
                    <Box display="flex" flexDirection="column">
                      <Typography
                        variant="HM1"
                        fontWeight={FONT_WEIGHTS.SEMIBOLD}
                      >
                        Failed ({createdOn})
                      </Typography>
                      <Typography variant="BM1">Cathedral Plumbing</Typography>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails className={classes.accordionDetails}>
                    {issues.map((issue: IIssue) => {
                      return (
                        <InspectionIssueCard
                          key={issue.id}
                          redTag={issue}
                          inspectionTrade={inspectionTrade}
                          resolvedByTrade={tradesMap[issue.resolvedByTradeId]}
                          causedByTrade={tradesMap[issue.causedByTradeId]}
                          onEdit={handleUpdateIssue}
                          onDelete={handleDeleteIssue}
                          tradeOptions={tradeOptions}
                        />
                      );
                    })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
        )}
        {selectedTab === prepWorkTab.value && (
          <Box>
            <MiSectionHeader
              variant={SECTION_HEADER_VARIANTS.UNDERLINED}
              title="Prep Work"
            />
          </Box>
        )}
        {selectedTab === historyTab.value && (
          <Box display="flex" flexDirection="column" flex={1} overflow="hidden">
            <MiSectionHeader
              title="History"
              leftIcon={mdiHistory}
              variant={SECTION_HEADER_VARIANTS.UNDERLINED}
            />
            <MiActivityLog
              activities={inspectionActivities || []}
              isLoading={loading}
              dateRange={historyDateRange}
              onDateRangeChange={handleHistoryDateRangeChange}
            />
          </Box>
        )}
      </>
    </Box>
  );
};

const useStyles = makeStyles()((theme) => ({
  scheduleSectionField: {
    flex: 1,
  },
  divider: {
    height: 26,
    alignSelf: "center",
    margin: theme.spacing(0, 2),
  },
  accordion: {
    boxShadow: "none",
  },
  accordionSummary: {
    backgroundColor: theme.palette.error.light,
    border: `1px solid ${theme.palette.error.main}`,
    borderRadius: 2,
  },
  accordionDetails: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
    padding: theme.spacing(2, 0, 0),
  },
}));
